<template>
  <div class="row">
    <div v-if="submerchant && statistics" class="col-md-12">
      <div class="row">
        <div class="col-md-12">
          <submerchant-navigation :submerchant-id="submerchantId"></submerchant-navigation>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <h2 class="card-header">Driver performance</h2>
            <div class="card-body">
              <div class="row">
                <div class="col-md-8">
                  <h5 class="card-title">
                    {{ submerchant.name }}
                    <span v-if="submerchant.reference">
                      <code>{{ submerchant.reference }}</code>
                    </span>
                  </h5>
                  <h6 class="card-subtitle mb-4 text-muted">
                    <span v-if="statistics.calculatedAt">
                      Statistics calculated
                      <time
                        :datetime="statistics.calculatedAt"
                      >{{ statistics.calculatedAt | moment('from', 'now') }}</time>
                    </span>
                  </h6>
                </div>

                <div class="col-md-4">
                  <label for="selectedPeriod" class="form-label">Time period</label>

                  <select v-model="selectedPeriod" class="form-control" id="selectedPeriod">
                    <option value="allTime">All time</option>
                    <option value="twelveMonths">Past 12 months</option>
                    <option value="thirtyDays">Past 30 days</option>
                    <option value="sevenDays">Past 7 days</option>
                    <option value="twentyFourHours">Past 24 hours</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <!-- Transaction Volume -->
        <div class="col-md-3">
          <stats-card
            type="warning"
            icon="fa fa-credit-card"
            small-title="Transaction volume"
            :title="statistics.sales[selectedPeriod].currentPeriod.summary.valueSum | money('gbp')"
          >
            <div class="stats text-right" slot="footer">
              <span v-if="statistics.sales[selectedPeriod].currentPeriod.changeVsPrevious">
                Change:
                {{ statistics.sales[selectedPeriod].currentPeriod
                .changeVsPrevious.valueSum | percentageChange }} &#37;
              </span>
            </div>
          </stats-card>
        </div>

        <!-- Transaction count -->
        <div class="col-md-3">
          <stats-card
            type="success"
            icon="fa fa-plus-square"
            small-title="Transaction count"
            :title="statistics.sales[selectedPeriod].currentPeriod.summary.count | number()"
          >
            <div class="stats text-right" slot="footer">
              <span v-if="statistics.sales[selectedPeriod].currentPeriod.changeVsPrevious">
                Change:
                {{ statistics.sales[selectedPeriod].currentPeriod
                .changeVsPrevious.count | percentageChange }} &#37;
              </span>
            </div>
          </stats-card>
        </div>

        <!-- Average transaction value -->
        <div class="col-md-3">
          <stats-card
            type="danger"
            icon="fa fa-calculator"
            small-title="Average transaction value"
            :title="statistics.sales[selectedPeriod].currentPeriod.summary.valueAvg | money('gbp')"
          >
            <div class="stats text-right" slot="footer">
              <span v-if="statistics.sales[selectedPeriod].currentPeriod.changeVsPrevious">
                Change:
                {{ statistics.sales[selectedPeriod].currentPeriod
                .changeVsPrevious.valueAvg | percentageChange }} &#37;
              </span>
            </div>
          </stats-card>
        </div>

        <!-- Highest transaction value -->
        <div class="col-md-3">
          <stats-card
            type="info"
            icon="fa fa-arrow-up"
            small-title="Highest transaction value"
            :title="statistics.sales[selectedPeriod].currentPeriod.summary.valueMax | money('gbp')"
          >
            <div class="stats text-right" slot="footer">
              <span v-if="statistics.sales[selectedPeriod].currentPeriod.changeVsPrevious">
                Change:
                {{ statistics.sales[selectedPeriod].currentPeriod
                .changeVsPrevious.valueMax | percentageChange }} &#37;
              </span>
            </div>
          </stats-card>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header">
              <div class="row">
                <div class="col-md-4 offset-md-8">
                  <label for="salesChartMetric" class="form-label">Metric</label>

                  <select v-model="salesChartMetric" class="form-control" id="salesChartMetric">
                    <option value="valueSum">Transaction volume</option>
                    <option value="count">Transaction count</option>
                    <option value="valueAvg">Average transaction value</option>
                    <option value="valueMax">Highest transaction value</option>
                  </select>
                </div>
              </div>
            </div>
            <chart-card-body
              v-if="salesChart.data"
              :chart-data="salesChart.data"
              :chart-options="salesChart.options"
              :key="salesChart.key"
            ></chart-card-body>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-4">
          <chart-card
            v-if="cardSchemeChart.data"
            :chart-data="cardSchemeChart.data"
            chart-type="Doughnut"
            color="#fbc658"
            chart-title="Card schemes (transaction count)"
            :title-label="null"
            :chart-options="{  title: { display: true, text: 'Card schemes (transaction count)' }, legend: { display: true, position: 'bottom' }, tooltips: { enabled: true } }"
            :key="cardSchemeChart.key"
          ></chart-card>
        </div>

        <div class="col-md-4">
          <chart-card
            v-if="cardTypeChart.data"
            :chart-data="cardTypeChart.data"
            chart-type="Doughnut"
            color="#fbc658"
            chart-title="Card types (transaction count)"
            :title-label="null"
            :chart-options="{  title: { display: true, text: 'Card types (transaction count)' }, legend: { display: true, position: 'bottom' }, tooltips: { enabled: true } }"
            :key="cardTypeChart.key"
          ></chart-card>
        </div>

        <div class="col-md-4">
          <chart-card
            v-if="cardholderTypeChart.data"
            :chart-data="cardholderTypeChart.data"
            chart-type="Doughnut"
            color="#fbc658"
            chart-title="Cardholder types (transaction count)"
            :title-label="null"
            :chart-options="{  title: { display: true, text: 'Cardholder types (transaction count)' }, legend: { display: true, position: 'bottom' }, tooltips: { enabled: true } }"
            :key="cardholderTypeChart.key"
          ></chart-card>
        </div>
      </div>
    </div>
    <div v-else>
      <div v-if="loading" class="spinner-container">
        <double-bounce size="60px"></double-bounce>
      </div>
    </div>
  </div>
</template>
  
<script>
import humanizeString from "string-humanize";
import moment from "moment";

import { DoubleBounce } from "vue-loading-spinner";
import SubmerchantNavigation from "./SubmerchantNavigation";
import ChartCard from "src/components/UIComponents/Cards/ChartCard";
import ChartCardBody from "src/components/UIComponents/Cards/ChartCardBody";
import StatsCard from "src/components/UIComponents/Cards/StatsCard";
import Badge from "src/components/UIComponents/Badge";
import SalesList from "src/components/Dashboard/Views/Sales/SalesList";
import Card from "src/components/UIComponents/Cards/Card";

export default {
  name: "SubmerchantPage",
  components: {
    DoubleBounce,
    SubmerchantNavigation,
    ChartCard,
    Badge,
    SalesList,
    Card,
    ChartCardBody,
    StatsCard
  },
  data() {
    return {
      error: null,
      loading: null,
      submerchant: null,
      statistics: null,
      selectedPeriod: "thirtyDays",
      salesChartMetric: "valueSum",
      salesChart: {
        data: null,
        key: 0
      },
      cardSchemeChart: {
        data: null,
        key: 0
      },
      cardTypeChart: {
        data: null,
        key: 0
      },
      cardholderTypeChart: {
        data: null,
        key: 0
      }
    };
  },
  props: ["submerchantId"],
  watch: {
    // whenever question changes, this function will run
    selectedPeriod: function(newValue, oldValue) {
      this.drawSalesChart();
      this.drawCardSchemeChart();
      this.drawCardTypeChart();
      this.drawCardholderTypeChart();
    },
    salesChartMetric: function(newValue, oldValue) {
      this.drawSalesChart();
      this.drawCardSchemeChart();
      this.drawCardTypeChart();
      this.drawCardholderTypeChart();
    }
  },
  methods: {
    drawCardSchemeChart: function() {
      let selectedPeriod = this.selectedPeriod;

      if (this.selectedPeriod === "allTime") {
        selectedPeriod = "twelveMonths";
      }

      this.cardSchemeChart = {
        data: {
          datasets: [
            {
              data: _.map(
                this.statistics.sales[selectedPeriod].currentPeriod.summary
                  .cardSchemes,
                (scheme, key) => {
                  return scheme.count;
                }
              ),
              backgroundColor: _.map(
                this.statistics.sales[selectedPeriod].currentPeriod.summary
                  .cardSchemes,
                (scheme, key) => {
                  if (key === "american_express") {
                    return "#0372CA";
                  } else if (key === "mastercard") {
                    return "#FE5D22";
                  } else if (key === "visa") {
                    return "#1D2966";
                  } else if (key === "unknown") {
                    return "#ddd"; // TODO: set this properly
                  } else {
                    return "#000"; // TODO: set random hex
                  }
                }
              ),
              hoverBackgroundColor: _.map(
                this.statistics.sales[selectedPeriod].currentPeriod.summary
                  .cardSchemes,
                (scheme, key) => {
                  if (key === "american_express") {
                    return "#acdafe";
                  } else if (key === "mastercard") {
                    return "#ca3701";
                  } else if (key === "visa") {
                    return "#374ec3";
                  } else if (key === "unknown") {
                    return "#ddd"; // TODO: set this properly
                  } else {
                    return "#000"; // TODO: set random hex
                  }
                }
              )
            }
          ],
          labels: _.map(
            this.statistics.sales[selectedPeriod].currentPeriod.summary
              .cardSchemes,
            (scheme, key) => {
              return scheme.label;
            }
          )
        },
        key: this.cardSchemeChart + 1
      };
    },
    drawCardTypeChart: function() {
      let selectedPeriod = this.selectedPeriod;

      if (this.selectedPeriod === "allTime") {
        selectedPeriod = "twelveMonths";
      }

      this.cardTypeChart = {
        data: {
          datasets: [
            {
              data: _.map(
                this.statistics.sales[selectedPeriod].currentPeriod.summary
                  .cardTypes,
                (scheme, key) => {
                  return scheme.count;
                }
              ),
              backgroundColor: _.map(
                this.statistics.sales[selectedPeriod].currentPeriod.summary
                  .cardTypes,
                (scheme, key) => {
                  if (key === "credit") {
                    return "#4D4D4D";
                  } else if (key === "debit") {
                    return "#5DA5DA";
                  } else if (key === "unknown") {
                    return "#FAA43A";
                  } else {
                    return "#000"; // TODO: set random hex
                  }
                }
              ),
              hoverBackgroundColor: _.map(
                this.statistics.sales[selectedPeriod].currentPeriod.summary
                  .cardTypes,
                (scheme, key) => {
                  if (key === "credit") {
                    return "#4D4D4D";
                  } else if (key === "debit") {
                    return "#5DA5DA";
                  } else if (key === "unknown") {
                    return "#FAA43A";
                  } else {
                    return "#000"; // TODO: set random hex
                  }
                }
              )
            }
          ],
          labels: _.map(
            this.statistics.sales[selectedPeriod].currentPeriod.summary
              .cardTypes,
            (scheme, key) => {
              return scheme.label;
            }
          )
        },
        key: this.cardTypeChart + 1
      };
    },
    drawCardholderTypeChart: function() {
      let selectedPeriod = this.selectedPeriod;

      if (this.selectedPeriod === "allTime") {
        selectedPeriod = "twelveMonths";
      }

      this.cardholderTypeChart = {
        data: {
          datasets: [
            {
              data: _.map(
                this.statistics.sales[selectedPeriod].currentPeriod.summary
                  .cardholderTypes,
                (scheme, key) => {
                  return scheme.count;
                }
              ),
              backgroundColor: _.map(
                this.statistics.sales[selectedPeriod].currentPeriod.summary
                  .cardholderTypes,
                (scheme, key) => {
                  if (key === "consumer") {
                    return "#60BD68";
                  } else if (key === "commercial") {
                    return "#F17CB0";
                  } else if (key === "unknown") {
                    return "#B276B2";
                  } else {
                    return "#000"; // TODO: set random hex
                  }
                }
              ),
              hoverBackgroundColor: _.map(
                this.statistics.sales[selectedPeriod].currentPeriod.summary
                  .cardholderTypes,
                (scheme, key) => {
                  if (key === "consumer") {
                    return "#60BD68";
                  } else if (key === "commercial") {
                    return "#F17CB0";
                  } else if (key === "unknown") {
                    return "#B276B2";
                  } else {
                    return "#000"; // TODO: set random hex
                  }
                }
              )
            }
          ],
          labels: _.map(
            this.statistics.sales[selectedPeriod].currentPeriod.summary
              .cardholderTypes,
            (scheme, key) => {
              return scheme.label;
            }
          )
        },
        key: this.cardholderTypeChart + 1
      };
    },
    drawSalesChart: function() {
      let selectedPeriod = this.selectedPeriod;

      if (this.selectedPeriod === "allTime") {
        selectedPeriod = "twelveMonths";
      }

      // chart labels
      let datasetLabelFormat = "DD MMM";
      let xAxisLabelFormat = "ddd";
      let datapointFormat = function(datapoint) {
        return datapoint / 100; // pence to pounds
      };

      // title
      let title;
      if (this.salesChartMetric === "valueSum") {
        title = "Transaction volume";
      } else if (this.salesChartMetric === "count") {
        title = "Transaction count";

        datapointFormat = function(datapoint) {
          return datapoint;
        };
      } else if (this.salesChartMetric === "valueAvg") {
        title = "Average tranasction value";
      } else if (this.salesChartMetric === "valueMax") {
        title = "Highest transaction";
      }

      title += ` (past ${humanizeString(selectedPeriod).toLowerCase()})`;

      // set chart labels based on selected period
      if (selectedPeriod === "twelveMonths") {
        datasetLabelFormat = "MMM YYYY";
        xAxisLabelFormat = "MMM";
      } else if (selectedPeriod === "thirtyDays") {
        datasetLabelFormat = "DD MMM";
        xAxisLabelFormat = "DD";
      } else if (selectedPeriod === "sevenDays") {
        datasetLabelFormat = "DD MMM";
        xAxisLabelFormat = "ddd";
      } else if (selectedPeriod === "twentyFourHours") {
        datasetLabelFormat = "DD MMM HH A";
        xAxisLabelFormat = "HH";
      }

      this.salesChart = {
        options: {
          title: { display: true, text: title },
          legend: {
            position: "bottom",
            display: true
          },
          tooltips: {
            enabled: true,
            mode: "label",
            xPadding: 10,
            yPadding: 10,
            bodySpacing: 10
          }
        },
        data: {
          labels: _.map(
            this.statistics.sales[selectedPeriod].currentPeriod.segments,
            item => {
              return moment(item.startDate).format(xAxisLabelFormat);
            }
          ),
          datasets: [
            {
              label: `${moment(
                _.first(
                  this.statistics.sales[selectedPeriod].currentPeriod.segments
                ).startDate
              ).format(datasetLabelFormat)} - ${moment(
                _.last(
                  this.statistics.sales[selectedPeriod].currentPeriod.segments
                ).startDate
              ).format(datasetLabelFormat)}`,
              fill: false,
              borderColor: "#332288",
              pointBackgroundColor: "#332288",
              backgroundColor: "rgba(0, 0, 0, 0)",
              data: _.map(
                this.statistics.sales[selectedPeriod].currentPeriod.segments,
                item => {
                  return datapointFormat(item.summary[this.salesChartMetric]);
                }
              )
            },
            {
              label: `${moment(
                _.first(
                  this.statistics.sales[selectedPeriod].previousPeriod.segments
                ).startDate
              ).format(datasetLabelFormat)} - ${moment(
                _.last(
                  this.statistics.sales[selectedPeriod].previousPeriod.segments
                ).startDate
              ).format(datasetLabelFormat)}`,
              fill: false,
              borderColor: "#88CCEE",
              pointBackgroundColor: "#88CCEE",
              backgroundColor: "rgba(0, 0, 0, 0)",
              data: _.map(
                this.statistics.sales[selectedPeriod].previousPeriod.segments,
                item => {
                  return datapointFormat(item.summary[this.salesChartMetric]);
                }
              )
            }
          ]
        },
        key: this.salesChart.key + 1 // this will re-ender the component (see https://michaelnthiessen.com/force-re-render/)
      };
    }
  },
  beforeMount() {
    this.loading = true;
    this.axios
      .get(`/v1/submerchants/${this.submerchantId}`)
      .then(response => {
        this.submerchant = response.data;

        // TODO: set limited fields if possible
        return this.axios.get(
          `/v1/submerchants/${this.submerchantId}/statistics`
        );
      })
      .then(response => {
        this.statistics = response.data;

        this.drawSalesChart();
        this.drawCardSchemeChart();
        this.drawCardTypeChart();
        this.drawCardholderTypeChart();
      })
      .catch(error => {
        this.error = "Failed to load data";
      })
      .finally(() => {
        this.loading = false;
      });
  }
};
</script>
